import { useState } from "react";
import { submitContactForm } from "./services/api";
import {
  Search,
  Rocket,
  FileText,
  Mail,
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import LoginNav from "./components/LoginNav";

const CATEGORIES = [
  {
    id: "getting-started",
    label: "Getting Started",
    icon: Rocket,
    description:
      "New to ApplicantHQ? Learn how to set up your profile, upload your resume, and land your first tailored application.",
    highlight: false,
  },
  {
    id: "resume-tailoring",
    label: "FAQ",
    icon: FileText,
    description:
      "Understand how resume tailoring works, how ATS scores are calculated, and how to improve your match rate.",
    highlight: true,
  },
  {
    id: "contact",
    label: "Contact & Account",
    icon: Mail,
    description:
      "Have a question, found a bug, or want to share feedback? Reach out to our team - we typically respond within 24-48 hours.",
    highlight: false,
  },
];

const FAQS = {
  "getting-started": [
    {
      q: "How does ApplicantHQ work?",
      a: "ApplicantHQ automates your job application process by tailoring your resume to each job description, calculating ATS match scores, and helping you apply faster. Simply upload your resume, paste a job URL, and let our app generate the perfect tailored resume for you.",
    },
    {
      q: "What file formats are supported?",
      a: "We support PDF, DOC, DOCX, and TXT file formats. For best results, we recommend using PDF files.",
    },
    {
      q: "How do I get started?",
      a: "1. Create an account or sign in\n2. Upload your master resume\n3. Paste a job posting URL or description\n4. Review the tailored resume\n5. Download and apply!",
    },
    {
      q: "How do I upload my resume?",
      a: "Go to your Profile page and click 'Upload Resume'. You can upload a PDF, DOCX, or TXT file. Once uploaded, it becomes your master resume used for all tailoring.",
    },
  ],
  "resume-tailoring": [
    {
      q: "How accurate is the ATS scoring?",
      a: "Our ATS scorer analyzes keyword matching, formatting, and relevance to provide a match percentage. While not 100% identical to real ATS systems, it gives you a strong indicator of your compatibility.",
    },
    {
      q: "Can I edit the tailored resume?",
      a: "Yes! After generating a tailored resume, you can download it as a PDF and edit it in any PDF or word processor. We recommend reviewing and personalizing it before applying.",
    },
    {
      q: "How long does tailoring take?",
      a: "Resume tailoring typically takes 15–30 seconds depending on the complexity of the job description and current system load.",
    },
    {
      q: "How can I improve my ATS score?",
      a: "Include keywords from the job description, match your skills to required qualifications, use standard section headings, quantify achievements with numbers, and keep formatting simple and clean.",
    },
    {
      q: "Is ApplicantHQ free?",
      a: "ApplicantHQ is currently free during our beta phase. We may introduce premium features in the future.",
    },
    {
      q: "Is my data secure?",
      a: "Yes! We use industry-standard encryption and never share your personal information or resumes with third parties. Your data is stored securely on MongoDB Atlas.",
    },
    {
      q: "How do I report a bug?",
      a: "Use the contact form below and select 'Bug Report' as the subject. Please describe what you were doing and what went wrong — screenshots are very helpful!",
    },
  ],
  contact: [],
};

const POPULAR_TOPICS = [
  { label: "Getting Started", category: "getting-started" },
  { label: "Resume Tailoring", category: "resume-tailoring" },
  { label: "ATS Score", category: "resume-tailoring" },
  { label: "Contact Us", category: "contact" },
];

const HelpScreen = ({
  userData,
  userEmail,
  navigateTo,
  handleErrors,
  isAuthenticated,
  currentScreen,
}) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: userEmail || "",
    subject: "Testimonial",
    message: "",
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus("sending");
    try {
      const response = await submitContactForm(contactForm);
      if (!response.error) {
        setSubmitStatus("success");
        setContactForm({
          name: "",
          email: userEmail || "",
          subject: "Testimonial",
          message: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      setSubmitStatus("error");
      await handleErrors("Error submitting contact form", error);
    }
  };

  const allFaqs = Object.entries(FAQS).flatMap(([cat, qs]) =>
    qs.map((q) => ({ ...q, category: cat })),
  );
  const searchResults =
    searchQuery.trim().length > 1
      ? allFaqs.filter(
          (f) =>
            f.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
            f.a.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : [];

  return (
    <div style={{ background: "#f9fafb", minHeight: "100%" }}>
      {!isAuthenticated && (
        <LoginNav navigateTo={navigateTo} currentScreen={currentScreen} />
      )}

      {/* Hero */}
      <div style={{ textAlign: "center", padding: "4rem 1rem 2rem" }}>
        <h1 className="applicant-theme-text">Help Center</h1>
        <p
          style={{
            fontSize: "1.05rem",
            color: "#6b7280",
            marginBottom: "2rem",
          }}
        >
          Find answers to your questions and get the most out of ApplicantHQ.
        </p>

        {/* Search */}
        <div
          style={{
            position: "relative",
            maxWidth: "38rem",
            margin: "0 auto 1.5rem",
          }}
        >
          <Search
            size={18}
            style={{
              position: "absolute",
              left: "1rem",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9ca3af",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            placeholder="Search your keyword"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setActiveCategory(null);
            }}
            style={{
              width: "100%",
              padding: "0.875rem 1rem 0.875rem 2.75rem",
              fontSize: "1rem",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              outline: "none",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              background: "#fff",
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#6366f1";
              e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e5e7eb";
              e.target.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)";
            }}
          />
        </div>

        {/* Popular topics */}
        {!searchQuery && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: "0.875rem",
                color: "#6b7280",
                marginRight: "0.25rem",
              }}
            >
              Popular topics:
            </span>
            {POPULAR_TOPICS.map((t) => (
              <button
                key={t.label}
                onClick={() => setActiveCategory(t.category)}
                style={{
                  background:
                    activeCategory === t.category ? "#eef2ff" : "#fff",
                  border: `1px solid ${activeCategory === t.category ? "#a5b4fc" : "#e5e7eb"}`,
                  color: activeCategory === t.category ? "#4f46e5" : "#374151",
                  padding: "0.3rem 0.9rem",
                  borderRadius: "999px",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Search results */}
      {searchQuery.trim().length > 1 && (
        <div
          style={{
            maxWidth: "44rem",
            margin: "0 auto 3rem",
            padding: "0 1rem",
          }}
        >
          {searchResults.length === 0 ? (
            <p style={{ textAlign: "center", color: "#6b7280" }}>
              No results found for "{searchQuery}".
            </p>
          ) : (
            searchResults.map((faq, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "1rem 1.25rem",
                  marginBottom: "0.75rem",
                }}
              >
                <p
                  style={{
                    fontWeight: 600,
                    color: "#111827",
                    marginBottom: "0.4rem",
                  }}
                >
                  {faq.q}
                </p>
                <p
                  style={{
                    color: "#6b7280",
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                  }}
                >
                  {faq.a.split("\n")[0]}
                </p>
              </div>
            ))
          )}
        </div>
      )}

      {/* Category cards */}
      {!searchQuery && !activeCategory && (
        <div
          style={{
            maxWidth: "62rem",
            margin: "0 auto 4rem",
            padding: "0 1.5rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "1.5rem",
          }}
        >
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <div
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  background: cat.highlight
                    ? "linear-gradient(135deg, #402c7a 0%, rgb(130, 109, 192) 100%)"
                    : "#fff",
                  border: `1px solid ${cat.highlight ? "#402c7a" : "#e5e7eb"}`,
                  borderRadius: "16px",
                  padding: "2rem 1.75rem",
                  cursor: "pointer",
                  transition: "box-shadow 0.15s, transform 0.15s",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: "1rem",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 8px 28px rgba(79,70,229,0.18)";
                  e.currentTarget.style.transform = "translateY(-3px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "none";
                }}
              >
                <div
                  style={{
                    width: "3.25rem",
                    height: "3.25rem",
                    borderRadius: "50%",
                    background: cat.highlight
                      ? "rgba(255, 255, 255, 0.3)"
                      : "#f4eeff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon
                    size={24}
                    style={{ color: cat.highlight ? "#fff" : "#402c7a" }}
                  />
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: cat.highlight ? "#fff" : "#111827",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {cat.label}
                  </h3>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: cat.highlight
                        ? "rgba(255, 255, 255, 0.9)"
                        : "#6b7280",
                      lineHeight: 1.6,
                    }}
                  >
                    {cat.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail view */}
      {!searchQuery && activeCategory && (
        <div
          style={{
            maxWidth: "48rem",
            margin: "0 auto 4rem",
            padding: "0 1.5rem",
          }}
        >
          <button
            onClick={() => {
              setActiveCategory(null);
              setExpandedFaq(null);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              background: "none",
              border: "none",
              color: "#6b7280",
              cursor: "pointer",
              fontSize: "0.875rem",
              marginBottom: "1.5rem",
              padding: 0,
            }}
          >
            <ArrowLeft size={15} /> Back
          </button>

          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: 700,
              color: "#111827",
              marginBottom: "0.5rem",
            }}
          >
            {CATEGORIES.find((c) => c.id === activeCategory)?.label}
          </h2>
          <p style={{ color: "#6b7280", marginBottom: "2rem" }}>
            {CATEGORIES.find((c) => c.id === activeCategory)?.description}
          </p>

          {/* FAQ accordion */}
          {FAQS[activeCategory] && activeCategory !== "contact" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                marginBottom: "2rem",
              }}
            >
              {FAQS[activeCategory].map((faq, i) => (
                <div
                  key={i}
                  style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "1rem 1.25rem",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      gap: "1rem",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#111827",
                        fontSize: "0.95rem",
                      }}
                    >
                      {faq.q}
                    </span>
                    {expandedFaq === i ? (
                      <ChevronUp
                        size={16}
                        style={{ color: "#6b7280", flexShrink: 0 }}
                      />
                    ) : (
                      <ChevronDown
                        size={16}
                        style={{ color: "#6b7280", flexShrink: 0 }}
                      />
                    )}
                  </button>
                  {expandedFaq === i && (
                    <div
                      style={{
                        padding: "0 1.25rem 1rem",
                        borderTop: "1px solid #f3f4f6",
                      }}
                    >
                      {faq.a.split("\n").map((line, j) => (
                        <p
                          key={j}
                          style={{
                            color: "#6b7280",
                            lineHeight: 1.7,
                            margin: "0.4rem 0",
                            fontSize: "0.9rem",
                          }}
                        >
                          {line}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Contact form */}
          {activeCategory === "contact" && (
            <>
              {FAQS["contact"].map((faq, i) => (
                <div
                  key={i}
                  style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    overflow: "hidden",
                    marginBottom: "0.75rem",
                  }}
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "1rem 1.25rem",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      gap: "1rem",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#111827",
                        fontSize: "0.95rem",
                      }}
                    >
                      {faq.q}
                    </span>
                    {expandedFaq === i ? (
                      <ChevronUp
                        size={16}
                        style={{ color: "#6b7280", flexShrink: 0 }}
                      />
                    ) : (
                      <ChevronDown
                        size={16}
                        style={{ color: "#6b7280", flexShrink: 0 }}
                      />
                    )}
                  </button>
                  {expandedFaq === i && (
                    <div
                      style={{
                        padding: "0 1.25rem 1rem",
                        borderTop: "1px solid #f3f4f6",
                      }}
                    >
                      <p
                        style={{
                          color: "#6b7280",
                          lineHeight: 1.7,
                          fontSize: "0.9rem",
                        }}
                      >
                        {faq.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}

              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "16px",
                  padding: "1.75rem",
                  marginTop: "1.5rem",
                }}
              >
                <h3
                  style={{
                    fontWeight: 700,
                    color: "#111827",
                    marginBottom: "1.25rem",
                  }}
                >
                  Send us a message
                </h3>
                <form
                  onSubmit={handleContactSubmit}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#374151",
                        marginBottom: "0.4rem",
                      }}
                    >
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, name: e.target.value })
                      }
                      placeholder="Your name"
                      style={{
                        width: "100%",
                        padding: "0.625rem 0.875rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "0.9rem",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#374151",
                        marginBottom: "0.4rem",
                      }}
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          email: e.target.value,
                        })
                      }
                      placeholder="your.email@example.com"
                      style={{
                        width: "100%",
                        padding: "0.625rem 0.875rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "0.9rem",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#374151",
                        marginBottom: "0.4rem",
                      }}
                    >
                      Subject *
                    </label>
                    <select
                      required
                      value={contactForm.subject}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          subject: e.target.value,
                        })
                      }
                      style={{
                        width: "100%",
                        padding: "0.625rem 0.875rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "0.9rem",
                        boxSizing: "border-box",
                        outline: "none",
                        background: "#fff",
                      }}
                    >
                      <option value="Testimonial">Testimonial</option>
                      <option value="Bug">Bug Report</option>
                      <option value="Feature">Feature Request</option>
                      <option value="Support">Technical Support</option>
                      <option value="Feedback">General Feedback</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#374151",
                        marginBottom: "0.4rem",
                      }}
                    >
                      Message *
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={contactForm.message}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          message: e.target.value,
                        })
                      }
                      placeholder="What would you like to tell us?"
                      style={{
                        width: "100%",
                        padding: "0.625rem 0.875rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "0.9rem",
                        boxSizing: "border-box",
                        outline: "none",
                        resize: "vertical",
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitStatus === "sending"}
                    className="buttons-indigo"
                  >
                    {submitStatus === "sending" ? "Sending…" : "Send Message"}
                  </button>
                  {submitStatus === "success" && (
                    <p style={{ color: "#16a34a", fontSize: "0.875rem" }}>
                      Message sent! We'll get back to you within 24–48 hours.
                    </p>
                  )}
                  {submitStatus === "error" && (
                    <p style={{ color: "#dc2626", fontSize: "0.875rem" }}>
                      Failed to send. Please email us directly at
                      support@theapplicanthq.com
                    </p>
                  )}
                </form>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpScreen;
